/**
 * PreDepartureChecksCard - Expandable card for pre-departure checks
 */

import React from 'react';
import { FileText, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const PreDepartureChecksCard = ({ 
  preDepartureChecks,
  setPreDepartureChecks,
  hasPassengers,
  isExpanded,
  onToggle,
  isComplete
}) => {
  return (
    <div className={`rounded-lg border-2 transition-all ${
      isComplete ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
    }`}>
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-gray-600'}`} />
            <div>
              <h4 className="text-sm font-medium">Pre-Departure Checks</h4>
              <p className="text-xs text-gray-500">
                {isComplete ? 'Completed' : 'Pending'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isComplete && <CheckCircle className="h-5 w-5 text-green-600" />}
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-3 mt-4">
            <p className="text-xs text-gray-600">
              Complete all required pre-departure checks as per WBC3 requirements
            </p>
            
            {/* Basic checks - always required */}
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={preDepartureChecks.navigationLights || false}
                  onChange={(e) => setPreDepartureChecks(prev => ({
                    ...prev,
                    navigationLights: e.target.checked
                  }))}
                  className="rounded"
                />
                <span className="text-sm">Navigation lights tested</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={preDepartureChecks.steering || false}
                  onChange={(e) => setPreDepartureChecks(prev => ({
                    ...prev,
                    steering: e.target.checked
                  }))}
                  className="rounded"
                />
                <span className="text-sm">Steering tested (hard to port/starboard)</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={preDepartureChecks.engineTest || false}
                  onChange={(e) => setPreDepartureChecks(prev => ({
                    ...prev,
                    engineTest: e.target.checked
                  }))}
                  className="rounded"
                />
                <span className="text-sm">Engine tested ahead/astern</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={preDepartureChecks.safetyEquipment || false}
                  onChange={(e) => setPreDepartureChecks(prev => ({
                    ...prev,
                    safetyEquipment: e.target.checked
                  }))}
                  className="rounded"
                />
                <span className="text-sm">Safety equipment verified present and accessible</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={preDepartureChecks.sternGland || false}
                  onChange={(e) => setPreDepartureChecks(prev => ({
                    ...prev,
                    sternGland: e.target.checked
                  }))}
                  className="rounded"
                />
                <span className="text-sm">Stern gland checked for leaks</span>
              </label>

              {hasPassengers && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={preDepartureChecks.passengerBriefing || false}
                    onChange={(e) => setPreDepartureChecks(prev => ({
                      ...prev,
                      passengerBriefing: e.target.checked
                    }))}
                    className="rounded"
                  />
                  <span className="text-sm">Passenger safety briefing completed</span>
                </label>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreDepartureChecksCard;